/**
 * ce-search nav integration. Fetches results from the custom hybrid search
 * service and renders real theme product cards (pattern__product-card--default,
 * server-rendered via the Section Rendering API) into the nav search dropdown
 * (desktop) and search pane (mobile), replacing Shopify predictive search.
 *
 * When a hit carries matched_variant (e.g. "blue sheets" matched the Pacific
 * Blue variant), the card is rendered for that variant via ?variant=.
 *
 * Also reports analytics to ce-search: a per-browser-session id rides along
 * on every search, and result clicks beacon to /v1/analytics/click.
 *
 * ce-search is the default nav search (ABT-237 concluded; arm b won),
 * invoked from main-nav.liquid whenever the "ce-search API URL" theme
 * setting is set. Clearing that setting reverts everyone to native
 * predictive search.
 */
window.ceSearchNav = (function () {
  var controller = null;

  var CARD_SECTION = "meta-pattern-product-card";

  // list-none: the rendered <li> cards sit in these grid <ul>s; without it the
  // browser shows default list bullets. className is set directly (overwriting
  // any class on the <ul>), so the reset has to live here.
  var LAYOUTS = {
    desktop: {
      grid: "mt-1 grid w-full list-none grid-cols-4 gap-2 xl:mt-2 2xl:gap-5",
      empty: "w-full list-none py-32 text-center text-2xl xl:py-36",
    },
    mobile: {
      grid: "mt-3 grid list-none grid-cols-2 justify-evenly gap-x-2 gap-y-4",
      empty: "w-full list-none py-10 text-center text-lg",
    },
  };

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function sessionId() {
    try {
      var sid = sessionStorage.getItem("ce_sid");
      if (!sid) {
        sid =
          window.crypto && crypto.randomUUID
            ? crypto.randomUUID()
            : "sid-" + Date.now() + "-" + Math.random().toString(36).slice(2);
        sessionStorage.setItem("ce_sid", sid);
      }
      return sid;
    } catch (e) {
      return ""; // storage blocked: search still works, analytics lose the session
    }
  }

  // Order attribution: stamp the ce-search session id onto the cart as the
  // ce_search_session note attribute so it rides through to the order and the
  // orders/create webhook can join it back to searches. Once per session,
  // fire-and-forget - attribution must never affect search.
  function stampCartSession() {
    try {
      var sid = sessionId();
      if (!sid || sessionStorage.getItem("ce_sid_stamped")) return;
      sessionStorage.setItem("ce_sid_stamped", "1");
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: { ce_search_session: sid } }),
      }).catch(function () {
        try {
          sessionStorage.removeItem("ce_sid_stamped");
        } catch (e) {}
      });
    } catch (e) {
      /* storage blocked: skip attribution */
    }
  }

  function trackClick(apiUrl, query, hit, position) {
    try {
      var payload = new Blob(
        [
          JSON.stringify({
            query: query,
            product_id: String(hit.id),
            position: position,
            session_id: sessionId() || undefined,
          }),
        ],
        // text/plain is CORS-safelisted: no preflight, so the beacon survives
        // the click navigating away (a JSON-typed beacon is dropped after it).
        { type: "text/plain;charset=UTF-8" }
      );
      navigator.sendBeacon(apiUrl.replace(/\/$/, "") + "/v1/analytics/click", payload);
    } catch (e) {
      /* analytics must never break navigation */
    }
  }

  function imageUrl(url, width) {
    if (!url) return "";
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + width;
  }

  function price(hit) {
    var fmt = function (n) {
      var s = n.toLocaleString("en-US", { style: "currency", currency: "USD" });
      return s.replace(/\.00$/, "");
    };
    if (hit.price_min === hit.price_max) return fmt(hit.price_min);
    return "From " + fmt(hit.price_min);
  }

  /** Minimal fallback card, used only when the section render fails. */
  function fallbackCard(hit) {
    var variant = hit.matched_variant;
    var img = (variant && variant.image_url) || hit.image_url;
    var href = "/products/" + esc(hit.handle) + (variant ? "?variant=" + esc(variant.id) : "");
    var imgHtml = img
      ? '<img src="' +
        esc(imageUrl(img, 400)) +
        '" alt="' +
        esc(hit.title) +
        '" loading="lazy" class="aspect-7/9 w-full object-cover">'
      : '<div class="aspect-7/9 w-full bg-ce-white-200"></div>';
    return (
      '<a href="' +
      href +
      '" class="block">' +
      imgHtml +
      '<h3 class="mt-2 font-work-sans text-sm">' +
      esc(hit.title) +
      "</h3>" +
      '<p class="text-sm font-light">' +
      esc(price(hit)) +
      "</p>" +
      "</a>"
    );
  }

  /**
   * The card's Alpine component ships in pattern__product-card.js, normally
   * loaded by the script_tag inside the snippet — but scripts inserted via
   * innerHTML never execute. Load it once, manually, from the src the
   * section render advertises, and wait for it before returning card HTML.
   * The shared promise also prevents nav and search results from racing to
   * load the component independently.
   */
  function ensureCardScript(fetchedDoc) {
    if (window.__productCardScriptReady) return Promise.resolve();
    if (window.__productCardScriptPromise) return window.__productCardScriptPromise;

    var tag = fetchedDoc.querySelector('script[src*="pattern__product-card.js"]');
    if (!tag) return Promise.reject(new Error("product card script not found"));

    window.__productCardScriptPromise = new Promise(function (resolve, reject) {
      var script = document.createElement("script");
      script.src = tag.getAttribute("src");
      script.addEventListener("load", resolve, { once: true });
      script.addEventListener(
        "error",
        function () {
          window.__productCardScriptPromise = null;
          reject(new Error("product card script failed to load"));
        },
        { once: true },
      );
      document.head.appendChild(script);
    });

    return window.__productCardScriptPromise;
  }

  /** Server-render the real theme card for a hit (variant-aware). */
  function fetchCard(hit, signal) {
    var params = "section_id=" + CARD_SECTION;
    if (hit.matched_variant) params += "&variant=" + encodeURIComponent(hit.matched_variant.id);
    return fetch("/products/" + encodeURIComponent(hit.handle) + "?" + params, { signal: signal })
      .then(function (res) {
        // 404 = product unpublished/removed from the storefront since the last
        // index sync. Drop it entirely (null) rather than render a dead card.
        if (res.status === 404) return null;
        if (!res.ok) throw new Error("section render " + res.status);
        return res.text();
      })
      .then(function (html) {
        if (html === null) return null;
        var doc = new DOMParser().parseFromString(html, "text/html");
        var card = doc.querySelector("article");
        if (!card) throw new Error("no card in section render");
        return ensureCardScript(doc).then(function () {
          return card.outerHTML;
        });
      })
      .catch(function (err) {
        if (err && err.name === "AbortError") throw err;
        return fallbackCard(hit);
      });
  }

  // Quick links (promoted links) for non-product intents like "returns" or
  // "track order". Rendered above the product grid in the dropdown, additive to
  // product results and shown even when there are no product hits. The JS fully
  // owns the container's visibility (no Alpine x-show) to avoid conflicts.
  // Markup mirrors the /search results page's "Looking for" banner
  // (renderLinks in ce-search-results.js) — a bare underlined link here read
  // as a heading for the product grid, not a page suggestion.
  function renderNavLinks(el, links) {
    if (!el) return;
    if (!links || !links.length) {
      el.innerHTML = "";
      el.style.display = "none";
      return;
    }
    el.style.display = "";
    var anchors = links
      .map(function (l) {
        return (
          '<a href="' +
          esc(l.url) +
          '" class="text-sm font-medium text-ce-gray-800 underline underline-offset-2 hover:text-ce-gray-700">' +
          esc(l.title) +
          " &rarr;</a>"
        );
      })
      .join('<span class="text-ce-gray-700" aria-hidden="true">&middot;</span>');
    el.innerHTML =
      '<div class="flex flex-wrap items-center gap-x-4 gap-y-2 rounded border border-ce-gray-300 bg-ce-white-200 px-4 py-3 font-work-sans">' +
      '<span class="text-sm text-ce-gray-700">Looking for</span>' +
      anchors +
      "</div>";
  }

  function renderInto(el, layout, query, hits, cardsHtml, apiUrl) {
    el.className = layout.grid;
    // The redesigned desktop overlay (…-search-output, no -b suffix) puts its
    // "Show All" in the suggestion column, so omit the in-grid one there to
    // match the native arm (c) and avoid a duplicate. The old modal (-b ids)
    // and both mobile panes keep the in-grid "Show All" — that matches their
    // native counterparts.
    var showAll =
      layout === LAYOUTS.desktop && !/-b$/.test(el.id)
        ? ""
        : '<li class="list-none" style="grid-column:1/-1">' +
          '<a href="/search?q=' +
          encodeURIComponent(query) +
          '" class="my-2 block w-full text-end">Show All &rarr;</a>' +
          "</li>";
    el.innerHTML =
      cardsHtml
        .map(function (html, i) {
          return '<li class="list-none" data-ce-pos="' + (i + 1) + '">' + html + "</li>";
        })
        .join("") + showAll;

    el.querySelectorAll("li[data-ce-pos]").forEach(function (li) {
      li.addEventListener(
        "click",
        function () {
          var pos = parseInt(li.dataset.cePos, 10);
          trackClick(apiUrl, query, hits[pos - 1], pos);
        },
        { capture: true }
      );
    });
  }

  /**
   * @param {string} apiUrl   base URL of the ce-search API
   * @param {string} query    the search query
   * @param {Array<{id: string, layout: 'desktop'|'mobile'}>} targets
   * @returns {Promise<void>} resolves when results are rendered
   */
  function render(apiUrl, query, targets) {
    if (controller) controller.abort();
    var thisController = (controller = new AbortController());

    // Clear any prior quick links immediately so stale ones don't linger while
    // the new request is in flight.
    targets.forEach(function (target) {
      renderNavLinks(document.getElementById(target.id + "-links"), null);
    });

    var params = new URLSearchParams({ q: query, limit: "4", filters: "available:true" });
    var sid = sessionId();
    if (sid) params.set("session_id", sid);
    stampCartSession();

    return fetch(apiUrl.replace(/\/$/, "") + "/v1/search?" + params.toString(), {
      signal: thisController.signal,
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (thisController.signal.aborted) return;
        // Quick links render regardless of product hits (an intent query like
        // "returns" may return few or no products but should still link out).
        targets.forEach(function (target) {
          renderNavLinks(document.getElementById(target.id + "-links"), data.links);
        });
        if (!data.hits || data.hits.length === 0) {
          targets.forEach(function (target) {
            var el = document.getElementById(target.id);
            if (!el) return;
            var layout = LAYOUTS[target.layout] || LAYOUTS.desktop;
            el.className = "list-none";
            el.innerHTML =
              '<li class="list-none"><p class="' +
              layout.empty +
              '">No results for &quot;' +
              esc(query) +
              "&quot;</p></li>";
          });
          return;
        }
        return Promise.all(
          data.hits.map(function (hit) {
            return fetchCard(hit, thisController.signal);
          })
        ).then(function (cardsHtml) {
          if (thisController.signal.aborted) return;
          // Drop hits whose card came back null (404 since last sync) so a stale
          // result never leaves an empty grid cell. Keep hits/cards aligned.
          var keptHits = [],
            keptCards = [];
          for (var i = 0; i < cardsHtml.length; i++) {
            if (cardsHtml[i]) {
              keptHits.push(data.hits[i]);
              keptCards.push(cardsHtml[i]);
            }
          }
          targets.forEach(function (target) {
            var el = document.getElementById(target.id);
            if (!el) return;
            var layout = LAYOUTS[target.layout] || LAYOUTS.desktop;
            if (keptCards.length === 0) {
              el.className = "list-none";
              el.innerHTML =
                '<li class="list-none"><p class="' +
                layout.empty +
                '">No results for &quot;' +
                esc(query) +
                "&quot;</p></li>";
              return;
            }
            renderInto(el, layout, query, keptHits, keptCards, apiUrl);
          });
        });
      })
      .catch(function (err) {
        if (err && err.name === "AbortError") return;
        // Service unreachable: leave existing content; native /search via the
        // form submit remains the fallback path.
        console.warn("ce-search unavailable", err);
      });
  }

  return { render: render };
})();
